import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, onSnapshot, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { addDays, format, isBefore, startOfDay, subDays } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import { calculateFinalPrice } from '../../../utils/pricing';
import ClientLayout from '../../../components/layout/ClientLayout';
import {
  FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
  getFacilityReservationApplicationWindowEnd,
  getFacilityReservationApplicationWindowMessage,
  isWithinFacilityReservationApplicationWindow
} from '../../../utils/facilityReservationDate';

interface Reservation {
  id: string;
  uid: string;
  facilityName: string;
  facilityId: string;
  reservationDate: Date;
  startTime: number;
  endTime: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: Date;
  updatedAt?: Date;
  price?: number;
  statusHistory?: {
    status: string;
    changedAt: Date;
    changedBy: string;
    reason?: string;
  }[];
}

interface Facility {
  id: string;
  name: string;
  price: number;
  basePrice?: number;
  maxPrice?: number;
  reservationType: 'time-slot' | 'start-end';
  reservationDays: number;
  reservationWindowDays: number;
  reservationTimeUnit: number;
  operatingHours: {
    start: number;
    end: number;
  };
  operatingDays: number[];
  closedPeriods?: Array<{
    id: string;
    start: {
      date: string;
      time: number;
    };
    end: {
      date: string;
      time: number;
    };
  }>;
  availableTimeSlots?: number[];
  discounts?: Array<{
    memberType: string;
    discountRate: number;
  }>;
}

const CONTRACTOR_USER_LEVEL = 3; // 공사 관계자

const getEffectiveReservationDays = (facility: Facility, userLevel: number | null) => {
  if (userLevel === CONTRACTOR_USER_LEVEL) return 0;
  return facility.reservationDays ?? 0;
};

const getClosedTimeRangesForDate = (facility: Facility, date: Date): Array<[number, number]> => {
  const currentDay = startOfDay(date);
  const currentDayTime = currentDay.getTime();
  const ranges: Array<[number, number]> = [];

  if (!facility.closedPeriods || facility.closedPeriods.length === 0) return ranges;

  for (const period of facility.closedPeriods) {
    const periodStartDay = startOfDay(parseISO(period.start.date));
    const periodEndDay = startOfDay(parseISO(period.end.date));

    const periodStartTime = periodStartDay.getTime();
    const periodEndTime = periodEndDay.getTime();

    if (currentDayTime < periodStartTime || currentDayTime > periodEndTime) continue;

    let rangeStart = 0;
    let rangeEnd = 24;

    if (periodStartTime === periodEndTime) {
      rangeStart = period.start.time;
      rangeEnd = period.end.time;
    } else if (currentDayTime === periodStartTime) {
      rangeStart = period.start.time;
    } else if (currentDayTime === periodEndTime) {
      rangeEnd = period.end.time;
    }

    if (rangeStart < rangeEnd) {
      ranges.push([rangeStart, rangeEnd]);
    }
  }

  return ranges;
};

const getClosedTimeSlotsForDate = (facility: Facility, date: Date) => {
  const { start: operatingStart, end: operatingEnd } = facility.operatingHours;
  const unit = facility.reservationTimeUnit || 1;

  const closedTimes: { [key: number]: boolean } = {};
  const ranges = getClosedTimeRangesForDate(facility, date);

  for (const [rangeStart, rangeEnd] of ranges) {
    const overlapStart = Math.max(rangeStart, operatingStart);
    const overlapEnd = Math.min(rangeEnd, operatingEnd);

    for (let hour = overlapStart; hour < overlapEnd; hour += unit) {
      closedTimes[hour] = true;
    }
  }

  return closedTimes;
};

const hasReservableTimeOnDate = (facility: Facility, date: Date) => {
  const { start: operatingStart, end: operatingEnd } = facility.operatingHours;
  const unit = facility.reservationTimeUnit || 1;

  if (operatingStart >= operatingEnd) return false;

  const closedTimes = getClosedTimeSlotsForDate(facility, date);

  for (let hour = operatingStart; hour < operatingEnd; hour += unit) {
    if (!closedTimes[hour]) return true;
  }

  return false;
};

const VAT_RATE = 0.1;

const KOREAN_WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const formatKoreanDateWithDay = (date: Date) => {
  const weekday = KOREAN_WEEKDAYS[date.getDay()] || '';
  return `${format(date, 'yyyy년 M월 d일')}(${weekday})`;
};

const getReservationApprovedAt = (reservation: Reservation): Date => {
  return reservation.updatedAt ?? reservation.createdAt;
};

const FACILITY_PAYMENT_ACCOUNT = {
  bankName: '신한은행',
  accountNumberSafetyTraining: '140-014-896099', // 안전교육장
  accountNumberDefault: '140-014-895991', // 그 외
  holder: '(주)아스타아이비에스'
};

const getFacilityPaymentAccountNumber = (facilityName: string) => {
  return facilityName.includes('안전교육장')
    ? FACILITY_PAYMENT_ACCOUNT.accountNumberSafetyTraining
    : FACILITY_PAYMENT_ACCOUNT.accountNumberDefault;
};

const FacilityReservationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userLevel, setUserLevel] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const isEditModalOpenRef = useRef(false);
  const [editForm, setEditForm] = useState({
    reservationDate: '',
    startTime: 0,
    endTime: 0
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [existingReservations, setExistingReservations] = useState<Reservation[]>([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const facilityIdRef = useRef<string | null>(null);

  // 사용자 인증 상태 확인
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (!authUser) {
        navigate('/signin');
      } else {
        setCurrentUser(authUser);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    isEditModalOpenRef.current = isEditModalOpen;
  }, [isEditModalOpen]);

  // 사용자 레벨 가져오기 (예약가능일자 정책에 사용)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchUserLevel = async () => {
      try {
        const userRef = doc(db, 'user_profiles', currentUser.uid);
        const userSnap = await getDoc(userRef);
        setUserLevel(userSnap.exists() ? (userSnap.data().userLevel ?? 9) : 9);
      } catch (err) {
        console.error('사용자 레벨을 불러오는 중 오류가 발생했습니다:', err);
        setUserLevel(9);
      }
    };

    fetchUserLevel();
  }, [currentUser?.uid]);

  // 시설물 정보 가져오기
  const fetchFacilityInfo = async (facilityId: string) => {
    try {
      const facilityRef = doc(db, 'facilities', facilityId);
      const facilitySnap = await getDoc(facilityRef);
      
      if (facilitySnap.exists()) {
        const data = facilitySnap.data();
        
        // availableTimeSlots가 없는 경우 기본값 설정
        let availableTimeSlots = data.availableTimeSlots || [];
        
        // 시간대형인데 availableTimeSlots가 비어있는 경우, 운영시간 기반으로 생성
        if (data.reservationType === 'time-slot' && availableTimeSlots.length === 0) {
          const start = data.operatingHours?.start || 0;
          const end = data.operatingHours?.end || 24;
          const timeUnit = data.reservationTimeUnit || 1;
          
          // reservationTimeUnit에 따라 시간 간격 조정
          for (let hour = start; hour < end; hour += timeUnit) {
            availableTimeSlots.push(hour);
          }
        }
        
        setFacility({
          id: facilitySnap.id,
          name: data.name || '',
          price: data.price || 0,
          basePrice: data.basePrice,
          maxPrice: data.maxPrice || undefined,
          reservationType: data.reservationType || 'start-end',
          reservationDays: data.reservationDays ?? 0,
          reservationWindowDays: data.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
          availableTimeSlots: availableTimeSlots,
          operatingHours: data.operatingHours || { start: 0, end: 24 },
          operatingDays: data.operatingDays || [0, 1, 2, 3, 4, 5, 6],
          closedPeriods: data.closedPeriods || [],
          reservationTimeUnit: data.reservationTimeUnit || 1,
          discounts: data.discounts || []
        });
        

      } else {
        console.error('시설물 정보가 존재하지 않습니다.');
      }
    } catch (err) {
      console.error('시설물 정보를 불러오는 중 오류가 발생했습니다:', err);
    }
  };

  // 해당 날짜의 기존 예약 내역 가져오기
  const fetchExistingReservations = async (date: Date, excludeReservationId?: string) => {
    try {
      const startOfSelectedDay = new Date(date);
      startOfSelectedDay.setHours(0, 0, 0, 0);
      
      const endOfSelectedDay = new Date(date);
      endOfSelectedDay.setHours(23, 59, 59, 999);

      const reservationsRef = collection(db, 'reservations');
      const q = query(
        reservationsRef,
        where('facilityId', '==', reservation?.facilityId),
        where('reservationDate', '>=', startOfSelectedDay),
        where('reservationDate', '<=', endOfSelectedDay),
        where('status', 'in', ['pending', 'approved'])
      );

      const querySnapshot = await getDocs(q);
      const reservations = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(res => res.id !== excludeReservationId) as Reservation[];

      setExistingReservations(reservations);
    } catch (err) {
      console.error('기존 예약 내역을 불러오는 중 오류가 발생했습니다:', err);
    }
  };

  // 예약 상세 정보 실시간 구독
  useEffect(() => {
    if (!currentUser || !id) return;

    setLoading(true);
    setError(null);

    const reservationRef = doc(db, 'reservations', id);
    const unsubscribe = onSnapshot(
      reservationRef,
      async (reservationSnap) => {
        try {
          if (!reservationSnap.exists()) {
            setReservation(null);
            setError('예약을 찾을 수 없습니다.');
            setLoading(false);
            return;
          }

          const data = reservationSnap.data();

          // 현재 로그인한 사용자의 예약인지 확인
          if (data.uid !== currentUser.uid) {
            setReservation(null);
            setError('접근 권한이 없습니다.');
            setLoading(false);
            return;
          }

          const reservationData: Reservation = {
            id: reservationSnap.id,
            uid: data.uid,
            facilityName: data.facilityName || '',
            facilityId: data.facilityId,
            reservationDate: data.reservationDate?.toDate ? data.reservationDate.toDate() : new Date(),
            startTime: data.startTime || 0,
            endTime: data.endTime || 0,
            status: data.status || 'pending',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate
              ? data.updatedAt.toDate()
              : data.updatedAt instanceof Date
                ? data.updatedAt
                : data.updatedAt
                  ? new Date(data.updatedAt)
                  : undefined,
            price: data.price ?? undefined,
          };

          // 상태 이력이 있는 경우 추가 (있을 때만)
          if (data.statusHistory && Array.isArray(data.statusHistory)) {
            reservationData.statusHistory = data.statusHistory.map((history: any) => ({
              status: history.status,
              changedAt: history.changedAt?.toDate
                ? history.changedAt.toDate()
                : history.changedAt instanceof Date
                  ? history.changedAt
                  : history.changedAt
                    ? new Date(history.changedAt)
                    : new Date(),
              changedBy: history.changedBy || '',
              reason: history.reason || ''
            }));
          }

          setReservation(reservationData);
          setLoading(false);

          // 시설물 정보는 facilityId 변경 시에만 다시 로드
          if (data.facilityId && facilityIdRef.current !== data.facilityId) {
            facilityIdRef.current = data.facilityId;
            await fetchFacilityInfo(data.facilityId);
          }

          // 모달이 열려있지 않을 때만 폼 초기화(사용자 입력 보호)
          if (!isEditModalOpenRef.current) {
            setEditForm({
              reservationDate: format(reservationData.reservationDate, 'yyyy-MM-dd'),
              startTime: reservationData.startTime,
              endTime: reservationData.endTime
            });
          }
        } catch (err) {
          console.error('예약 정보를 처리하는 중 오류가 발생했습니다:', err);
          setError('예약 정보를 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
        }
      },
      (err) => {
        console.error('예약 정보를 불러오는 중 오류가 발생했습니다:', err);
        setError('예약 정보를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, id]);

  // 날짜가 변경될 때마다 해당 날짜의 기존 예약 내역 가져오기
  useEffect(() => {
    if (editForm.reservationDate && reservation?.facilityId) {
      fetchExistingReservations(parseISO(editForm.reservationDate), reservation.id);
    }
  }, [editForm.reservationDate, reservation?.facilityId, reservation?.id]);

  // 시간 겹침 검사
  const isTimeOverlapping = (startTime: number, endTime: number): boolean => {
    return existingReservations.some(reservation => {
      return (startTime < reservation.endTime && endTime > reservation.startTime);
    });
  };

  // 시간대형인 경우 선택 가능한 시간대인지 검사
  const isValidTimeSlot = (time: number): boolean => {
    if (!facility || facility.reservationType !== 'time-slot') return true;
    return facility.availableTimeSlots?.includes(time) || false;
  };

  // 운영시간 내인지 검사
  const isWithinOperatingHours = (startTime: number, endTime: number): boolean => {
    if (!facility?.operatingHours) return true;
    return startTime >= facility.operatingHours.start && endTime <= facility.operatingHours.end;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reservation || !facility) return;

    try {
      setEditLoading(true);
      setEditError(null);

      if (reservation.status !== 'pending') {
        setEditError('관리자 승인 이전에만 예약 수정이 가능합니다.');
        setEditLoading(false);
        return;
      }

      const selectedDate = parseISO(editForm.reservationDate);
      const today = startOfDay(new Date());

      // 과거 날짜 체크
      if (isBefore(startOfDay(selectedDate), today)) {
        setEditError('과거 날짜는 선택할 수 없습니다.');
        setEditLoading(false);
        return;
      }

      // 예약가능일자(reservationDays) 체크
      const effectiveReservationDays = getEffectiveReservationDays(facility, userLevel);
      const minReservationDate = addDays(today, effectiveReservationDays);
      if (isBefore(startOfDay(selectedDate), minReservationDate)) {
        setEditError(
          effectiveReservationDays === 0
            ? '당일 이전으로는 예약을 수정할 수 없습니다.'
            : `예약은 ${effectiveReservationDays}일 후부터 가능합니다. 날짜를 다시 선택해주세요.`
        );
        setEditLoading(false);
        return;
      }

      if (!isWithinFacilityReservationApplicationWindow(
        selectedDate,
        today,
        effectiveReservationDays,
        facility.reservationWindowDays
      )) {
        setEditError(getFacilityReservationApplicationWindowMessage(facility.reservationWindowDays));
        setEditLoading(false);
        return;
      }

      // 운영요일 체크
      if (facility.operatingDays && facility.operatingDays.length > 0) {
        const selectedDayOfWeek = selectedDate.getDay();
        if (!facility.operatingDays.includes(selectedDayOfWeek)) {
          setEditError('선택한 날짜는 운영일이 아니어서 예약이 불가능합니다.');
          setEditLoading(false);
          return;
        }
      }

      // 휴무 기간으로 인해 예약 가능한 시간이 없는 날짜 체크
      if (!hasReservableTimeOnDate(facility, selectedDate)) {
        setEditError('선택한 날짜는 휴무로 인해 예약이 불가능합니다.');
        setEditLoading(false);
        return;
      }

      // 종료 시간이 시작 시간보다 이후인지 확인
      if (editForm.endTime <= editForm.startTime) {
        setEditError('종료 시간은 시작 시간보다 이후여야 합니다.');
        setEditLoading(false);
        return;
      }

      // 운영시간 체크
      if (!isWithinOperatingHours(editForm.startTime, editForm.endTime)) {
        setEditError(`운영시간(${facility.operatingHours?.start}시 ~ ${facility.operatingHours?.end}시) 내에 선택해주세요.`);
        setEditLoading(false);
        return;
      }

      // 시간대형인 경우 선택 가능한 시간대인지 확인
      if (facility.reservationType === 'time-slot') {
        if (!isValidTimeSlot(editForm.startTime) || !isValidTimeSlot(editForm.endTime)) {
          setEditError('선택 가능한 시간대만 선택해주세요.');
          setEditLoading(false);
          return;
        }
      }

      // 휴무 시간대 체크
      const closedTimes = getClosedTimeSlotsForDate(facility, selectedDate);
      if (facility.reservationType === 'time-slot') {
        if (closedTimes[editForm.startTime]) {
          setEditError('선택한 시간은 휴무로 인해 예약이 불가능합니다.');
          setEditLoading(false);
          return;
        }
      } else {
        for (let hour = editForm.startTime; hour < editForm.endTime; hour += 1) {
          if (closedTimes[hour]) {
            setEditError('선택한 시간 범위에 휴무 시간이 포함되어 예약이 불가능합니다.');
            setEditLoading(false);
            return;
          }
        }
      }

      // 시간 겹침 검사
      if (isTimeOverlapping(editForm.startTime, editForm.endTime)) {
        setEditError('선택한 시간에 이미 다른 예약이 있습니다.');
        setEditLoading(false);
        return;
      }

      const effectiveUserLevel = userLevel ?? 9;

      // 새로운 가격 계산 (부가세 포함 total 저장)
      const netPrice = calculateFinalPrice(
        {
          price: facility.price,
          basePrice: facility.basePrice,
          maxPrice: facility.maxPrice,
          discounts: facility.discounts
        },
        {
          reservationType: facility.reservationType,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
          reservationTimeUnit: facility.reservationTimeUnit
        },
        effectiveUserLevel
      );
      const vat = Math.round(netPrice * VAT_RATE);
      const totalPrice = netPrice + vat;

      const reservationRef = doc(db, 'reservations', reservation.id);
      await updateDoc(reservationRef, {
        reservationDate: selectedDate,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        price: totalPrice,
        updatedAt: new Date()
      });

      // 상태 업데이트
      setReservation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          reservationDate: selectedDate,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
          price: totalPrice,
          updatedAt: new Date()
        };
      });

      setIsEditModalOpen(false);
    } catch (err) {
      console.error('예약 수정 중 오류가 발생했습니다:', err);
      setEditError('예약 수정 중 오류가 발생했습니다.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!reservation || !currentUser) return;
    const canCancel =
      reservation.status === 'pending' || (reservation.status === 'approved' && userLevel === CONTRACTOR_USER_LEVEL);
    if (!canCancel) return;

    try {
      setCancelLoading(true);
      setCancelError(null);

      const reservationRef = doc(db, 'reservations', reservation.id);
      const updatedAt = new Date();

      const nextStatusHistory = [
        ...(reservation.statusHistory || []),
        {
          status: 'cancelled',
          changedAt: updatedAt,
          changedBy: currentUser.uid,
          reason: userLevel === CONTRACTOR_USER_LEVEL ? '공사 관계자 취소' : '사용자 취소'
        }
      ];

      await updateDoc(reservationRef, {
        status: 'cancelled',
        statusHistory: nextStatusHistory,
        updatedAt
      });

      setReservation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: 'cancelled',
          statusHistory: nextStatusHistory,
          updatedAt
        };
      });

      setIsCancelModalOpen(false);
      alert('예약이 취소되었습니다.');
    } catch (err) {
      console.error('예약 취소 중 오류가 발생했습니다:', err);
      setCancelError('예약 취소 중 오류가 발생했습니다.');
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'approved':
        return '승인됨';
      case 'rejected':
        return '거절됨';
      case 'cancelled':
        return '취소됨';
      default:
        return status;
    }
  };

  const handleEditClick = () => {
    if (reservation?.status !== 'pending') return;
    setIsEditModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditError(null);
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="bg-red-50 rounded-lg p-6 max-w-md w-full">
            <p className="text-red-600 text-center">{error}</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!reservation) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="bg-yellow-50 rounded-lg p-6 max-w-md w-full">
            <p className="text-yellow-600 text-center">예약 정보를 찾을 수 없습니다.</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const paymentAmount = reservation.price ?? 0;
  const shouldShowPaymentGuide = reservation.status === 'approved' && paymentAmount !== 0;
  const approvedAt = startOfDay(getReservationApprovedAt(reservation));
  const rawPaymentDueDate = subDays(startOfDay(reservation.reservationDate), 3);
  const paymentDueDate = isBefore(rawPaymentDueDate, approvedAt) ? approvedAt : rawPaymentDueDate;
  const paymentAccountNumber = getFacilityPaymentAccountNumber(reservation.facilityName);

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-full max-w-[760px] mx-auto relative pt-8 px-4 sm:px-6 lg:px-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-slate-800 text-2xl sm:text-3xl lg:text-4xl font-extrabold font-['Pretendard'] leading-tight">
                예약 상세 정보
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                {reservation.status === 'pending' && (
                  <button
                    onClick={handleEditClick}
                    className="w-full sm:w-[130px] h-11 bg-[#155acb] text-white text-sm sm:text-base font-bold rounded-sm hover:bg-[#155acb]/90 transition-colors"
                  >
                    예약 수정
                  </button>
                )}
                {(reservation.status === 'pending' ||
                  (reservation.status === 'approved' && userLevel === CONTRACTOR_USER_LEVEL)) && (
                  <button
                    onClick={() => {
                      setCancelError(null);
                      setIsCancelModalOpen(true);
                    }}
                    className="w-full sm:w-[130px] h-11 bg-red-600 text-white text-sm sm:text-base font-bold rounded-sm hover:bg-red-700 transition-colors"
                  >
                    예약 취소
                  </button>
                )}
                <button
                  onClick={() => navigate('/facility/reservations')}
                  className="w-full sm:w-[130px] h-11 bg-blue-500 text-white text-sm sm:text-base font-bold rounded-sm hover:bg-blue-600 transition-colors"
                >
                  예약 목록으로
                </button>
              </div>
            </div>
            {/* 구분선 */}
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
            <div className="mt-8 flex items-center justify-between">
              <div className="text-[#1e2c49] text-lg sm:text-xl lg:text-2xl font-bold font-['Pretendard'] leading-tight">
                예약 내역을 확인해주세요
              </div>
            </div>
          </div>

          {reservation.status === 'approved' && userLevel !== CONTRACTOR_USER_LEVEL && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm sm:text-base text-blue-800 font-medium">
                관리자 승인 이후에는 예약 수정/취소가 불가능합니다. 취소가 필요하다면 관리자에게 연락해주세요.
              </p>
            </div>
          )}
          {reservation.status === 'approved' && userLevel === CONTRACTOR_USER_LEVEL && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm sm:text-base text-blue-800 font-medium">
                승인된 예약은 수정할 수 없으며, 직접 취소 후 재신청하시기 바랍니다.
              </p>
            </div>
          )}

          {shouldShowPaymentGuide && (
            <div className="mt-6 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-3">
                  대관료 입금 안내
                </h2>
                <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-4"></div>

                <div className="text-sm sm:text-base text-slate-700 whitespace-pre-line leading-relaxed">
                  {'대관 승인에 따라 아래와 같이 대관료를 납부하여 주시기 바랍니다.\n' +
                    '대관료 입금 확인 후 대관이 최종 확정되며, 기한 내 입금이 확인되지 않을 경우, 부득이 대관이 취소될 수 있으니 참고하여 주시기 바랍니다.'}
                </div>

                <div className="mt-5 text-sm sm:text-base text-slate-800 whitespace-pre-line leading-relaxed">
                  {`○ 대관료: 금 ${paymentAmount.toLocaleString()}원 (부가가치세 포함)\n` +
                    `○ 입금기한: ${formatKoreanDateWithDay(paymentDueDate)}까지\n` +
                    `○ 입금계좌: ${FACILITY_PAYMENT_ACCOUNT.bankName} ${paymentAccountNumber}\n` +
                    `                 (예금주: ${FACILITY_PAYMENT_ACCOUNT.holder})\n` +
                    `○ 입금자명: 신청자명(또는 기관명)으로 입금 요망`}
                </div>
              </div>
            </div>
          )}

          {/* 정보 카드 */}
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">시설명</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{reservation.facilityName}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 상태</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-bold font-['Pretendard'] ${getStatusColor(reservation.status)}`}>
                    {getStatusText(reservation.status)}
                  </span>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 날짜</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{format(reservation.reservationDate, 'yyyy년 MM월 dd일')}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">이용 시간</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{reservation.startTime}시 - {reservation.endTime}시</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 일시</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{format(reservation.createdAt, 'yyyy년 MM월 dd일 HH:mm')}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 가격</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{reservation.price != null ? `${reservation.price.toLocaleString()}원` : '가격 정보 없음'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 ID</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold break-all">{reservation.id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 상태 이력 */}
          {reservation.statusHistory && reservation.statusHistory.length > 0 && (
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                  상태 변경 이력
                </h2>
                <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
                <ul className="divide-y divide-gray-200">
                  {reservation.statusHistory.map((history, index) => (
                    <li key={index} className="py-3 sm:py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm sm:text-base font-medium text-gray-900">
                            {getStatusText(history.status)}
                          </p>
                          <p className="mt-1 text-xs sm:text-sm text-gray-500">
                            {format(history.changedAt, 'yyyy년 MM월 dd일 HH:mm')}
                          </p>
                          {history.reason && (
                            <p className="mt-1 text-xs sm:text-sm text-gray-500">
                              사유: {history.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 예약 취소 모달 */}
          {isCancelModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">예약 취소</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  예약을 취소하면 되돌릴 수 없습니다. 취소하시겠습니까?
                </p>

                {cancelError && (
                  <p className="mt-3 text-xs sm:text-sm text-red-600">{cancelError}</p>
                )}

                <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (cancelLoading) return;
                      setIsCancelModalOpen(false);
                      setCancelError(null);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    닫기
                  </button>
                  <button
                    type="button"
                    disabled={cancelLoading}
                    onClick={handleCancelReservation}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {cancelLoading ? '취소 처리 중...' : '예약 취소'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 수정 모달 */}
          {isEditModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">예약 수정</h3>
                <form onSubmit={handleEditSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">예약 날짜</label>
                      <input
                        type="date"
                        value={editForm.reservationDate}
                        onChange={(e) => setEditForm(prev => ({ ...prev, reservationDate: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm sm:text-base"
                        min={
                          facility
                            ? format(addDays(startOfDay(new Date()), getEffectiveReservationDays(facility, userLevel)), 'yyyy-MM-dd')
                            : format(new Date(), 'yyyy-MM-dd')
                        }
                        max={format(getFacilityReservationApplicationWindowEnd(new Date(), facility?.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS), 'yyyy-MM-dd')}
                      />
                    </div>

                    {facility?.reservationType === 'time-slot' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">이용 시간</label>
                        <select
                          value={editForm.startTime}
                          onChange={(e) => {
                            const time = parseInt(e.target.value);
                            setEditForm(prev => ({
                              ...prev,
                              startTime: time,
                              endTime: time + (facility?.reservationTimeUnit || 1)
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm sm:text-base"
                        >
                          {facility.availableTimeSlots && facility.availableTimeSlots.length > 0 ? (
                            facility.availableTimeSlots.map(time => (
                              <option key={time} value={time}>
                                {time}시 ~ {time + (facility?.reservationTimeUnit || 1)}시
                              </option>
                            ))
                          ) : (
                            <option value="">이용 가능한 시간대가 없습니다</option>
                          )}
                        </select>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">시작 시간</label>
                          <select
                            value={editForm.startTime}
                            onChange={(e) => setEditForm(prev => ({ ...prev, startTime: parseInt(e.target.value) }))}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm sm:text-base"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{i}시</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">종료 시간</label>
                          <select
                            value={editForm.endTime}
                            onChange={(e) => setEditForm(prev => ({ ...prev, endTime: parseInt(e.target.value) }))}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm sm:text-base"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{i}시</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>

                  {editError && (
                    <p className="mt-2 text-xs sm:text-sm text-red-600">{editError}</p>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={handleEditClose}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {editLoading ? '수정 중...' : '수정하기'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* 하단 여백 */}
          <div className="h-16 sm:h-32"></div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default FacilityReservationDetailPage; 
